import { isExecutableMode } from '../../hooks/use-skills-repository';
import { fmtBytes, mono, Pill } from '../proxy-vault/ui';

import type { RepositorySkillFileContent } from '../../hooks/use-skills-repository';
import type { RepositorySkillFile } from '../../lib/api-client';

interface Props {
  /** The selected index entry (path, size, sha256, mode) — null when nothing is selected. */
  entry: RepositorySkillFile | null;
  content: RepositorySkillFileContent | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
}

/**
 * Read-only preview of one file of a repository skill version.
 *
 * The file endpoint returns raw bytes with the server's content type; the hook
 * decodes textual types to `text` and leaves binaries as `null`, which renders
 * the same placeholder the git-skills viewer uses. The header states the mode
 * as the server sends it — an octal STRING — with an "exec" pill when the
 * execute bit is set; that pill is the one fact an admin most needs from a
 * bundle: whether it runs code.
 */
export function RepositoryFileViewer({ entry, content, status, error }: Props) {
  if (!entry) {
    return (
      <div style={{ padding: 16, color: 'var(--text-mute)', fontSize: 12 }}>
        Select a file to view its contents.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        data-testid="skrepo-file-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 16px',
          borderBottom: '1px solid var(--border)',
          fontSize: 11,
          color: 'var(--text-soft)',
          fontFamily: mono,
          flexShrink: 0,
        }}
      >
        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{entry.path}</span>
        <span>{fmtBytes(entry.size)}</span>
        <span>{entry.mode}</span>
        {isExecutableMode(entry.mode) && <Pill tone="warn">exec</Pill>}
        <span style={{ marginLeft: 'auto' }} title={entry.sha256}>
          sha256 {entry.sha256.slice(0, 12)}…
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {status === 'loading' && (
          <div style={{ padding: 16, color: 'var(--text-mute)', fontSize: 12 }}>Loading…</div>
        )}
        {status === 'error' && (
          <div style={{ padding: 16, color: 'var(--kind-failure)', fontSize: 12 }}>
            {error ?? 'Failed to load file.'}
          </div>
        )}
        {status === 'success' && content && content.text === null && (
          <div style={{ padding: 16, color: 'var(--text-mute)', fontSize: 12 }}>
            Binary file ({content.content_type}) — preview not available.
          </div>
        )}
        {status === 'success' && content && content.text !== null && (
          <pre
            data-testid="skrepo-file-text"
            style={{
              margin: 0,
              padding: 16,
              fontFamily: mono,
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--text)',
              background: 'var(--bg-alt)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {content.text}
          </pre>
        )}
      </div>
    </div>
  );
}
